import { Slot as SlotPrimitive } from 'radix-ui';
import { ComponentProps, PropsWithChildren, useCallback, useEffect } from 'react';

import { cn } from '@/shared/helpers';

import { PageContent } from '../layout';
import {
  useWizardContext,
  WizardProvider,
  WizardStepDefinition,
  WizardStepSubmitHandler,
} from './wizard-context';

export function WizardRoot({
  steps,
  children,
}: PropsWithChildren<{
  steps: WizardStepDefinition[];
}>) {
  return (
    <WizardProvider steps={steps}>
      <div className="flex min-h-0 flex-1 flex-col">{children}</div>
    </WizardProvider>
  );
}

export function WizardContent({ children, className, ...props }: ComponentProps<'main'>) {
  return (
    <PageContent variant="column" className="overflow-y-auto">
      <main className={cn('mx-auto flex w-full max-w-3xl flex-col gap-4', className)} {...props}>
        {children}
      </main>
    </PageContent>
  );
}

export type WizardStepProps = PropsWithChildren<{
  step: WizardStepDefinition;
  complete?: boolean;
  onSubmitStep?: WizardStepSubmitHandler;
}>;

/**
 * `WizardStep` uses Slot, so its child must be a single element that forwards
 * refs to a DOM node for scroll and focus management.
 */
export function WizardStep({ step, complete, onSubmitStep, children }: WizardStepProps) {
  const {
    currentStepId,
    isStepComplete,
    mode,
    registerStepElement,
    registerStepSubmitHandler,
    setCurrentStep,
    setStepComplete,
  } = useWizardContext();
  const isCurrent = step.id === currentStepId;
  const isComplete = isStepComplete(step.id);

  useEffect(() => {
    setStepComplete(step.id, Boolean(complete));
  }, [complete, setStepComplete, step.id]);

  useEffect(() => {
    registerStepSubmitHandler(step.id, onSubmitStep);
    return () => registerStepSubmitHandler(step.id, undefined);
  }, [onSubmitStep, registerStepSubmitHandler, step.id]);

  const setRef = useCallback(
    (element: HTMLElement | null) => {
      registerStepElement(step.id, element);
    },
    [registerStepElement, step.id],
  );

  const makeCurrent = () => {
    setCurrentStep(step.id, 'user');
  };

  return (
    <SlotPrimitive.Slot
      ref={setRef}
      tabIndex={-1}
      data-wizard-step={step.id}
      data-current={isCurrent ? 'true' : undefined}
      data-complete={isComplete ? 'true' : undefined}
      data-required={step.required ? 'true' : undefined}
      className={cn(
        'transition-opacity',
        (mode === 'freeform' || isCurrent) && 'opacity-100',
        mode === 'guided' && !isCurrent && 'opacity-50 focus-within:opacity-100 hover:opacity-75',
      )}
      onClickCapture={makeCurrent}
      onFocusCapture={makeCurrent}
    >
      {children}
    </SlotPrimitive.Slot>
  );
}
